// dashboard-access-storage.service.ts
// Persists the dashboardAccess (all page information) response in IndexedDB,
// keyed by moduleKey, so consumers like topnav can read it back without relying
// solely on the in-memory BehaviorSubject in EnhancementService.
import { Injectable } from '@angular/core';
import { openDB, DBSchema } from 'idb';

interface DashboardAccessRecord {
  moduleKey: string; // moduleKey stored as string for a stable key
  pages: any;        // the dashboardAccess data (array of page information)
}

interface DashboardAccessDB extends DBSchema {
  dashboard_access: {
    key: string;
    value: DashboardAccessRecord;
  };
}

@Injectable({ providedIn: 'root' })
export class DashboardAccessStorageService {
  private dbPromise = openDB<DashboardAccessDB>('dashboardAccessDB', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('dashboard_access')) {
        db.createObjectStore('dashboard_access', { keyPath: 'moduleKey' });
      }
    },
  });

  // Save the dashboardAccess data for a given moduleKey.
  async saveDashboardAccess(moduleKey: any, pages: any): Promise<void> {
    if (moduleKey == null || moduleKey === '') return;
    const db = await this.dbPromise;
    await db.put('dashboard_access', { moduleKey: String(moduleKey), pages });
  }

  // Read the dashboardAccess data back for a given moduleKey (null if absent).
  async getDashboardAccess(moduleKey: any): Promise<any> {
    if (moduleKey == null || moduleKey === '') return null;
    const db = await this.dbPromise;
    const record = await db.get('dashboard_access', String(moduleKey));
    return record?.pages ?? null;
  }

  async clearAll(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear('dashboard_access');
  }
}
